/**
 * Combat Module - Finalized 43 Spec Skill Definitions for 6 Classes
 * Includes 1 ~ 50 (+10 Enchanter Cap = 60) per-level scaling formulas!
 */

import { DamageType } from '../../core/Types';

export interface SkillDefinition {
  id: number;
  name: string;
  nameKr: string;
  spCost: number;         // Base Lv.1 SP cost
  spCostPerLv?: number;   // SP increase per skill level
  mpCost: number;         // Base Lv.1 MP cost
  mpCostPerLv?: number;   // MP increase per skill level
  concCost: number;       // Base Lv.1 Concentration reserved
  concCostPerLv?: number; // Concentration increase per skill level
  isToggle: boolean;      // True for Concentration auras/buffs
  castTime: number;       // 0 for instant, > 0 for casted
  cooldown: number;       // Base Lv.1 cooldown in seconds
  cdPerLv?: number;       // Cooldown reduction per skill level (e.g. -2.5s for Raise Dead)
  range: number;          // Base Lv.1 range/radius in tiles
  rangePerLv?: number;    // Range/radius increase per skill level
  damageType: DamageType;
  basePower: number;      // Base Lv.1 damage/heal/effect
  powerPerLv?: number;    // Damage/heal/effect increase per skill level
  durationBase?: number;  // Base CC/Debuff duration
  durationPerLv?: number; // Duration increase per skill level
  description: string;
  growthSummary: string;  // Summary of per-level growth
  icon: string;
}

export interface ScaledSkillMetrics {
  level: number;
  spCost: number;
  mpCost: number;
  concCost: number;
  cooldown: number;
  range: number;
  power: number;
  duration: number;
}

/**
 * Compute exact scaled values for a skill at a given effective level (1 ~ 60)
 */
export function getScaledSkillMetrics(def: SkillDefinition, effectiveLevel: number): ScaledSkillMetrics {
  const lvDelta = Math.max(0, effectiveLevel - 1);
  return {
    level: effectiveLevel,
    spCost: Math.max(0, Math.round(def.spCost + (def.spCostPerLv || 0) * lvDelta)),
    mpCost: Math.max(0, Math.round(def.mpCost + (def.mpCostPerLv || 0) * lvDelta)),
    concCost: Math.max(0, Math.round(def.concCost + (def.concCostPerLv || 0) * lvDelta)),
    cooldown: def.cooldown === 0 ? 0 : Math.max(1.0, Number((def.cooldown + (def.cdPerLv || 0) * lvDelta).toFixed(1))),
    range: Number((def.range + (def.rangePerLv || 0) * lvDelta).toFixed(2)),
    power: Math.round(def.basePower + (def.powerPerLv || 0) * lvDelta),
    duration: Number(((def.durationBase || 0) + (def.durationPerLv || 0) * lvDelta).toFixed(2)),
  };
}

export const CLASS_SKILL_DEFINITIONS: Record<string, Record<number, SkillDefinition>> = {
  // ==========================================================================
  // a. 🛡️ 팔라딘 (Paladin - tank) - 7종 전문스킬
  // ==========================================================================
  tank: {
    1: {
      id: 1, name: 'Cleave Swing', nameKr: '휘두르기',
      spCost: 10, spCostPerLv: 0.6, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 3.5, range: 1.5, rangePerLv: 0.05,
      damageType: 'slash', basePower: 30, powerPerLv: 3.5,
      description: '주변 광역 베기 타격. 스킬 레벨 상승 시 타격 반경과 데미지가 증가합니다.',
      growthSummary: '레벨당 반경 +0.05타일, 데미지 +3.5 / SP소모 +0.6',
      icon: '🌪️',
    },
    2: {
      id: 2, name: 'Taunt', nameKr: '타운트',
      spCost: 8, spCostPerLv: 0.4, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 5.0, range: 5.0,
      damageType: 'holy', basePower: 300, powerPerLv: 45, durationBase: 4.0, durationPerLv: 0.2,
      description: '적의 어그로를 강제 장악하고 도발 유지시간을 늘립니다.',
      growthSummary: '레벨당 도발유지 +0.2초, 어그로 +45 / SP소모 +0.4',
      icon: '📢',
    },
    3: {
      id: 3, name: 'Healing Chant', nameKr: '힐링챈트',
      spCost: 0, mpCost: 5, mpCostPerLv: 0.4, concCost: 15, concCostPerLv: 0.7,
      isToggle: true, castTime: 0, cooldown: 1.5, range: 8.0,
      damageType: 'holy', basePower: 15, powerPerLv: 2.5,
      description: '파티원의 체력을 3초마다 회복시키는 성가 유지 (집중력 보존 + 틱당 MP 소모).',
      growthSummary: '레벨당 틱힐 +2.5 HP / MP소모 +0.4, 집중소모 +0.7',
      icon: '💚',
    },
    4: {
      id: 4, name: 'Shield Slam', nameKr: '방패슬램',
      spCost: 12, spCostPerLv: 0.5, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 8.0, range: 1.8,
      damageType: 'crush', basePower: 25, powerPerLv: 2.0, durationBase: 1.5, durationPerLv: 0.08,
      description: '방패로 강타하여 기절(Stun)시킵니다. 아군 타겟팅 시 아군 기절!',
      growthSummary: '레벨당 스턴시간 +0.08초, 데미지 +2.0 / SP소모 +0.5',
      icon: '🛡️',
    },
    5: {
      id: 5, name: 'Vigor', nameKr: 'Vigor (기력찬가)',
      spCost: 0, mpCost: 0, concCost: 12, concCostPerLv: 0.6,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 2, powerPerLv: 0.25,
      description: '파티원의 초당 SP 재생량을 증가시킵니다 (집중력 보존).',
      growthSummary: '레벨당 SP재생 +0.25/초 / 집중소모 +0.6',
      icon: '⚡',
    },
    6: {
      id: 6, name: 'Pull', nameKr: '풀 (Pull)',
      spCost: 0, mpCost: 10, mpCostPerLv: 0.5, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 6.0, range: 4.0, rangePerLv: 0.12,
      damageType: 'holy', basePower: 5, powerPerLv: 0.5,
      description: '원거리 적 견인 + 소량 어그로 증가 + 전투 종료 시까지 개인 방어력 증가(중복 불가)!',
      growthSummary: '레벨당 사거리 +0.12타일, 개인방어 +0.5%, 어그로 증가 / MP소모 +0.5',
      icon: '🧲',
    },
    7: {
      id: 7, name: 'Raise Dead', nameKr: '레이즈데드',
      spCost: 0, mpCost: 50, concCost: 0,
      isToggle: false, castTime: 3.0, cooldown: 180, cdPerLv: -2.5, range: 6.0,
      damageType: 'holy', basePower: 30,
      description: '쓰러진 아군을 부활시킵니다 (스킬 레벨 상승 시 재사용 대기시간 단축).',
      growthSummary: '레벨당 재사용 대기시간 -2.5초 감소 (60렙 시 32.5초)',
      icon: '✨',
    },
  },

  // ==========================================================================
  // b. 🏹 레인저 (Ranger - ranged_dps) - 7종 전문스킬
  // ==========================================================================
  ranged_dps: {
    1: {
      id: 1, name: 'Multi-Shot', nameKr: '멀티샷',
      spCost: 14, spCostPerLv: 0.8, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 4.5, range: 7.0,
      damageType: 'thrust', basePower: 38, powerPerLv: 2.2,
      description: '전방에 다수의 화살을 동시 발사합니다 (10레벨마다 화살 수 +1발 증가).',
      growthSummary: '10레벨당 화살 +1발(최대 8발), 데미지 증가 / SP소모 +0.8',
      icon: '🏹',
    },
    2: {
      id: 2, name: 'Critical Shot', nameKr: '크리티컬샷',
      spCost: 15, spCostPerLv: 0.7, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 1.4, cooldown: 6.0, range: 8.0,
      damageType: 'thrust', basePower: 55, powerPerLv: 3.5,
      description: '급소를 조준하여 높은 치명타 확률과 배율로 단발 관통 사격을 가합니다.',
      growthSummary: '레벨당 치명타율 +0.8%, 치명배율 +0.02배 / SP소모 +0.7',
      icon: '🎯',
    },
    3: {
      id: 3, name: 'Wind Arrow', nameKr: '바람살',
      spCost: 0, mpCost: 0, concCost: 15, concCostPerLv: 0.7,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 0,
      damageType: 'thrust', basePower: 8, powerPerLv: 0.7,
      description: '바람의 가호로 평타 및 사격 스킬의 장전(시전) 시간을 단축합니다 (자버프).',
      growthSummary: '레벨당 장전시간 -0.7% 단축 (60렙 시 -49.3%) / 집중소모 +0.7',
      icon: '🍃',
    },
    4: {
      id: 4, name: 'Rubber Man', nameKr: '고무인간',
      spCost: 0, mpCost: 0, concCost: 15, concCostPerLv: 0.7,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 0,
      damageType: 'thrust', basePower: 6, powerPerLv: 0.6,
      description: '유연한 몸놀림으로 회피(Evade) 확률을 대폭 증가시킵니다 (자버프).',
      growthSummary: '레벨당 회피확률 +0.6% 증가 (60렙 시 +41.4%) / 집중소모 +0.7',
      icon: '🤸',
    },
    5: {
      id: 5, name: 'Mark', nameKr: '마크 (방어감소)',
      spCost: 0, mpCost: 10, mpCostPerLv: 0.5, concCost: 10, concCostPerLv: 0.6,
      isToggle: false, castTime: 0, cooldown: 4.0, range: 8.0,
      damageType: 'thrust', basePower: 6, powerPerLv: 0.5, durationBase: 20,
      description: '대상을 마킹하여 방어력을 감소시킵니다 (15레벨마다 마킹 가능 대상 수 증가).',
      growthSummary: '레벨당 방어력 -0.5% 감소, 대상 수 증가 / MP +0.5, 집중 +0.6',
      icon: '👁️',
    },
    6: {
      id: 6, name: 'Trap', nameKr: '트랩',
      spCost: 0, mpCost: 12, mpCostPerLv: 0.5, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 10.0, range: 5.5,
      damageType: 'thrust', basePower: 20, powerPerLv: 0.8, durationBase: 8.0,
      description: '덫을 투척하여 적의 이동속도를 대폭 감소(Snare)시킵니다.',
      growthSummary: '레벨당 이동속도 감소폭 -0.8% 증가 / MP소모 +0.5',
      icon: '🪤',
    },
    7: {
      id: 7, name: 'Stealth', nameKr: '스텔스',
      spCost: 10, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 60.0, cdPerLv: -0.8, range: 0,
      damageType: 'thrust', basePower: 10,
      description: '모습을 감추어 은신 상태에 돌입합니다 (레벨 상승 시 재사용 대기시간 감소).',
      growthSummary: '레벨당 재사용 대기시간 -0.8초 감소 (60렙 시 12.8초)',
      icon: '👤',
    },
  },

  // ==========================================================================
  // c. 🧙 마법사 (Wizard - caster_dps) - 7종 전문스킬 (기본평타: 지팡이 크러시)
  // ==========================================================================
  caster_dps: {
    1: {
      id: 1, name: 'Meteor', nameKr: '메테오',
      spCost: 0, mpCost: 25, mpCostPerLv: 1.4, concCost: 0,
      isToggle: false, castTime: 2.0, cooldown: 6.0, range: 8.0,
      damageType: 'holy', basePower: 55, powerPerLv: 4.5,
      description: '주력 광역 화력기! 운석 갯수(10레벨당 +1)와 화염 DoT 지속 피해가 폭발적으로 증가합니다.',
      growthSummary: '10레벨당 운석 +1개, 직격딜 +4.5, 화상 DoT +2.5/초 / MP소모 +1.4',
      icon: '☄️',
    },
    2: {
      id: 2, name: 'Rock Throw', nameKr: '바위투사 (루트)',
      spCost: 0, mpCost: 12, mpCostPerLv: 0.6, concCost: 0,
      isToggle: false, castTime: 1.2, cooldown: 5.0, range: 7.5,
      damageType: 'crush', basePower: 20, powerPerLv: 1.0, durationBase: 2.5, durationPerLv: 0.15,
      description: '데미지는 낮으나 대지의 암석으로 적의 발을 묶어 강력한 이동불가(Root)를 겁니다.',
      growthSummary: '레벨당 이동불가 시간 +0.15초 증가 (60렙 시 11.3초) / MP소모 +0.6',
      icon: '🪨',
    },
    3: {
      id: 3, name: 'Chain Lightning', nameKr: '체인라이트닝',
      spCost: 0, mpCost: 18, mpCostPerLv: 1.0, concCost: 0,
      isToggle: false, castTime: 1.5, cooldown: 4.0, range: 7.5,
      damageType: 'holy', basePower: 25, powerPerLv: 2.0, durationBase: 1.0, durationPerLv: 0.03,
      description: '다단히트 연쇄 감전(기절/시전방해). 단일 대상(1체) 타격 시 데미지가 2.0~3.8배로 대폭 증폭됩니다!',
      growthSummary: '10레벨당 연쇄 +1체, 감전스턴 증가, 단일대상 2.0~3.8배 증폭 / MP +1.0',
      icon: '⚡',
    },
    4: {
      id: 4, name: 'Frost Wind', nameKr: '프로스트윈드',
      spCost: 0, mpCost: 15, mpCostPerLv: 0.7, concCost: 0,
      isToggle: false, castTime: 1.3, cooldown: 5.0, range: 7.0,
      damageType: 'holy', basePower: 25, powerPerLv: 0.8, durationBase: 6.0,
      description: '데미지는 낮으나 혹한의 바람으로 범위 내 적들의 이동속도를 강력하게 감속시킵니다.',
      growthSummary: '레벨당 감속 효율 +0.8% 증가 (60렙 시 -72.2% 감속) / MP소모 +0.7',
      icon: '❄️',
    },
    5: {
      id: 5, name: 'Mental Focus', nameKr: '정신집중',
      spCost: 0, mpCost: 15, mpCostPerLv: 0.6, concCost: 15, concCostPerLv: 0.7,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 0,
      damageType: 'holy', basePower: 5, powerPerLv: 0.5,
      description: '정신을 집중하여 마법 치명타 발동 확률을 대폭 증가시킵니다 (토글 자버프).',
      growthSummary: '레벨당 마법 치명타율 +0.5% 증가 / MP소모 +0.6, 집중소모 +0.7',
      icon: '🧠',
    },
    6: {
      id: 6, name: 'Mana Shield', nameKr: '마나쉴드',
      spCost: 0, mpCost: 15, mpCostPerLv: 0.8, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 10.0, range: 0,
      damageType: 'holy', basePower: 20, powerPerLv: 0.8, durationBase: 60.0,
      description: '60초간 방어력을 높이고 피격 시 HP 대신 MP로 피해를 흡수합니다.',
      growthSummary: '레벨당 방어력 +0.4%, MP 피해흡수율 +0.8% 증가 / MP소모 +0.8',
      icon: '🔮',
    },
    7: {
      id: 7, name: 'Teleport', nameKr: '순간이동',
      spCost: 0, mpCost: 20, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 35.0, cdPerLv: -0.5, range: 6.0,
      damageType: 'holy', basePower: 0,
      description: '바라보는 방향으로 6타일 즉시 공간이동합니다 (레벨 상승 시 쿨타임 감소).',
      growthSummary: '레벨당 재사용 대기시간 -0.5초 감소 (60렙 시 5.5초)',
      icon: '🌀',
    },
  },

  // ==========================================================================
  // d. ✨ 클레릭 (Cleric - healer) - 7종 전문스킬 (DAoC 특화 복합 버프 담당)
  // ==========================================================================
  healer: {
    1: {
      id: 1, name: 'Holy Hand', nameKr: '홀리핸드',
      spCost: 0, mpCost: 15, mpCostPerLv: 0.9, concCost: 0,
      isToggle: false, castTime: 1.5, cooldown: 1.5, range: 8.0,
      damageType: 'holy', basePower: 80, powerPerLv: 12.0,
      description: '신성한 손길로 단일 대상의 HP를 대량 회복합니다 (피격/이동 시 시전 중단).',
      growthSummary: '레벨당 치유량 +12 HP 증가 (60렙 시 +788 HP) / MP소모 +0.9',
      icon: '🙏',
    },
    2: {
      id: 2, name: 'Instant Heal', nameKr: '인스턴트힐',
      spCost: 0, mpCost: 25, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 35.0, cdPerLv: -0.5, range: 8.0,
      damageType: 'holy', basePower: 35,
      description: '시전 시간 없이 즉시 대상 최대 HP의 35%를 긴급 회복합니다.',
      growthSummary: '레벨당 재사용 대기시간 -0.5초 감소 (60렙 시 5.5초)',
      icon: '💖',
    },
    3: {
      id: 3, name: 'Instant Group Heal', nameKr: '인스턴트 그룹힐',
      spCost: 0, mpCost: 45, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 60.0, cdPerLv: -0.8, range: 8.0,
      damageType: 'holy', basePower: 25,
      description: '시전 시간 없이 즉시 파티원 전원의 HP를 최대치의 25%만큼 회복합니다.',
      growthSummary: '레벨당 재사용 대기시간 -0.8초 감소 (60렙 시 12.8초)',
      icon: '💞',
    },
    4: {
      id: 4, name: 'Cleansing', nameKr: '클렌징',
      spCost: 0, mpCost: 10, mpCostPerLv: 0.4, concCost: 0,
      isToggle: false, castTime: 0.8, cooldown: 2.0, range: 8.0,
      damageType: 'holy', basePower: 40, powerPerLv: 1.0,
      description: '아군에게 걸린 디버프, 이동감속, 화상/독 DoT를 정화 해제합니다.',
      growthSummary: '레벨당 해제 확률 +1.0% 증가 (60렙 시 100%) / MP소모 +0.4',
      icon: '✨',
    },
    5: {
      id: 5, name: 'Spec STR+CON Buff', nameKr: '[특화] 힘+체력 버프',
      spCost: 0, mpCost: 15, mpCostPerLv: 0.6, concCost: 12, concCostPerLv: 0.5,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 8, powerPerLv: 1.2,
      description: '대상의 힘과 체력을 동시에 증가시킵니다 (수도승의 기본 단일 버프와 합산 중첩!).',
      growthSummary: '레벨당 힘·체력 각 +1.2 (60렙 시 +79) / MP +0.6, 집중 +0.5',
      icon: '💪',
    },
    6: {
      id: 6, name: 'Spec DEX+QUI Buff', nameKr: '[특화] 민첩+속도 버프',
      spCost: 0, mpCost: 15, mpCostPerLv: 0.6, concCost: 12, concCostPerLv: 0.5,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 8, powerPerLv: 1.2,
      description: '대상의 민첩과 속도를 동시에 증가시킵니다 (수도승의 기본 단일 버프와 합산 중첩!).',
      growthSummary: '레벨당 민첩·속도 각 +1.2 (60렙 시 +79) / MP +0.6, 집중 +0.5',
      icon: '⚡',
    },
    7: {
      id: 7, name: 'Spec INT/PIE/CHA Buff', nameKr: '[특화] 지/신/매 버프',
      spCost: 0, mpCost: 18, mpCostPerLv: 0.7, concCost: 14, concCostPerLv: 0.5,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 8, powerPerLv: 1.2,
      description: '대상의 지능·신앙·매력을 동시에 높여 최대 MP와 최대 집중력 풀을 확장합니다!',
      growthSummary: '레벨당 지·신·매 각 +1.2 (60렙 시 +79) / MP +0.7, 집중 +0.5',
      icon: '👑',
    },
  },

  // ==========================================================================
  // e. 🎵 바드 (Bard - buffer) - 7종 전문스킬
  // ==========================================================================
  buffer: {
    1: {
      id: 1, name: 'Whisper', nameKr: '귓속말 (단일메즈)',
      spCost: 0, mpCost: 12, mpCostPerLv: 0.6, concCost: 0,
      isToggle: false, castTime: 1.2, cooldown: 4.0, range: 7.5,
      damageType: 'holy', basePower: 0, durationBase: 8.0, durationPerLv: 0.5,
      description: '단일 적에게 속삭여 장시간 수면(Mez)에 빠뜨립니다 (피격 시 즉시 해제).',
      growthSummary: '레벨당 단일 메즈시간 +0.5초 증가 (60렙 시 37.5초) / MP소모 +0.6',
      icon: '💤',
    },
    2: {
      id: 2, name: 'Lullaby', nameKr: '자장가 (광역메즈)',
      spCost: 0, mpCost: 22, mpCostPerLv: 1.1, concCost: 0,
      isToggle: false, castTime: 1.8, cooldown: 8.0, range: 3.5,
      damageType: 'holy', basePower: 0, durationBase: 5.0, durationPerLv: 0.35,
      description: '범위 내 모든 적을 동시에 잠재우는 광역 수면(AoE Mez) 연주입니다.',
      growthSummary: '레벨당 광역 메즈시간 +0.35초 증가 (60렙 시 25.6초) / MP소모 +1.1',
      icon: '🎶',
    },
    3: {
      id: 3, name: 'Song of Focus', nameKr: '집중의노래 (MP재생)',
      spCost: 0, mpCost: 0, concCost: 15, concCostPerLv: 0.6,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 1.5, powerPerLv: 0.22,
      description: '파티원 전원의 초당 MP 재생률을 대폭 증가시킵니다 (집중력 보존).',
      growthSummary: '레벨당 초당 MP재생 +0.22 증가 (60렙 시 +14.5/초) / 집중 +0.6',
      icon: '💧',
    },
    4: {
      id: 4, name: 'Song of Regen', nameKr: '재생의노래 (HP재생)',
      spCost: 0, mpCost: 0, concCost: 15, concCostPerLv: 0.6,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 2.5, powerPerLv: 0.35,
      description: '파티원 전원의 초당 HP 재생률을 대폭 증가시킵니다 (집중력 보존).',
      growthSummary: '레벨당 초당 HP재생 +0.35 증가 (60렙 시 +23.1/초) / 집중 +0.6',
      icon: '💗',
    },
    5: {
      id: 5, name: 'Song of March', nameKr: '행군의노래 (이속)',
      spCost: 0, mpCost: 0, concCost: 12, concCostPerLv: 0.5,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 15, powerPerLv: 0.9,
      description: '비전투 시 파티원 전원의 이동속도를 대폭 증가시킵니다 (집중력 보존).',
      growthSummary: '레벨당 비전투 이속 +0.9% 증가 (60렙 시 +68.1%) / 집중 +0.5',
      icon: '🥾',
    },
    6: {
      id: 6, name: 'Express Delivery', nameKr: '특급배달 (인스턴트 스턴)',
      spCost: 0, mpCost: 18, mpCostPerLv: 0.9, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 12.0, range: 6.5,
      damageType: 'crush', basePower: 15, durationBase: 1.5, durationPerLv: 0.1,
      description: '다옥 민스트럴식 즉시 시전 고함 스턴기! 적의 주문을 끊고 기절시킵니다.',
      growthSummary: '레벨당 즉시 스턴시간 +0.1초 증가 (60렙 시 7.4초) / MP소모 +0.9',
      icon: '📯',
    },
    7: {
      id: 7, name: 'SOS', nameKr: 'SOS (긴급도주)',
      spCost: 0, mpCost: 40, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 180.0, cdPerLv: -2.0, range: 8.0,
      damageType: 'holy', basePower: 100, durationBase: 10.0,
      description: '10초간 파티 전원의 HP 비례 이속 감소 페널티를 무시하고 이동속도를 +100% 가속합니다!',
      growthSummary: '레벨당 재사용 대기시간 -2.0초 감소 (60렙 시 62초)',
      icon: '🚀',
    },
  },

  // ==========================================================================
  // f. 🥋 수도승 (Monk - monk_hybrid) - 8종 전문스킬 (DAoC 기본 단일 버프 담당)
  // ==========================================================================
  monk_hybrid: {
    1: {
      id: 1, name: 'Whirlwind Kick', nameKr: '선풍각',
      spCost: 10, spCostPerLv: 0.6, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 3.0, range: 1.4, rangePerLv: 0.05,
      damageType: 'crush', basePower: 28, powerPerLv: 3.2,
      description: '회전 발차기로 주변 적들을 타격합니다 (레벨 상승 시 반경과 위력 증가).',
      growthSummary: '레벨당 반경 +0.05타일, 데미지 +3.2 / SP소모 +0.6',
      icon: '🌪️',
    },
    2: {
      id: 2, name: 'Leg Sweep', nameKr: '다리걸기',
      spCost: 8, spCostPerLv: 0.4, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 5.0, range: 1.8,
      damageType: 'crush', basePower: 20, powerPerLv: 0.8, durationBase: 6.0,
      description: '하단을 후려쳐 적의 이동속도를 대폭 감소(Snare)시킵니다.',
      growthSummary: '레벨당 이동속도 감소폭 -0.8% 증가 (60렙 시 -67.2%) / SP소모 +0.4',
      icon: '🦵',
    },
    3: {
      id: 3, name: 'No Mind', nameKr: '무념무상 (공속)',
      spCost: 0, mpCost: 12, mpCostPerLv: 0.5, concCost: 15, concCostPerLv: 0.6,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 0,
      damageType: 'holy', basePower: 10, powerPerLv: 0.7,
      description: '무아지경에 돌입하여 자신의 공격속도를 대폭 증가시킵니다 (자버프).',
      growthSummary: '레벨당 공격속도 +0.7% 증가 (60렙 시 +51.3%) / MP +0.5, 집중 +0.6',
      icon: '🧘',
    },
    4: {
      id: 4, name: 'Base Defense Buff', nameKr: '[기본] 방어버프',
      spCost: 0, mpCost: 0, concCost: 8, concCostPerLv: 0.35,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 5, powerPerLv: 0.4,
      description: '대상의 물리 방어력을 증가시킵니다 (다옥 기본 버프 라인).',
      growthSummary: '레벨당 방어력 +0.4% 증가 (60렙 시 +28.6%) / 집중소모 +0.35',
      icon: '🛡️',
    },
    5: {
      id: 5, name: 'Base STR Buff', nameKr: '[기본] 힘버프',
      spCost: 0, mpCost: 0, concCost: 8, concCostPerLv: 0.35,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 6, powerPerLv: 1.0,
      description: '대상의 힘(STR)을 증가시킵니다 (클레릭 특화 힘+체력 버프와 합산 중첩!).',
      growthSummary: '레벨당 힘 +1.0 증가 (60렙 시 +65) / 집중소모 +0.35',
      icon: '✊',
    },
    6: {
      id: 6, name: 'Base DEX Buff', nameKr: '[기본] 민첩버프',
      spCost: 0, mpCost: 0, concCost: 8, concCostPerLv: 0.35,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 6, powerPerLv: 1.0,
      description: '대상의 민첩(DEX)을 증가시킵니다 (클레릭 특화 민첩+속도 버프와 합산 중첩!).',
      growthSummary: '레벨당 민첩 +1.0 증가 (60렙 시 +65) / 집중소모 +0.35',
      icon: '🪶',
    },
    7: {
      id: 7, name: 'Base CON Buff', nameKr: '[기본] 체력버프',
      spCost: 0, mpCost: 0, concCost: 8, concCostPerLv: 0.35,
      isToggle: true, castTime: 0, cooldown: 1.0, range: 8.0,
      damageType: 'holy', basePower: 6, powerPerLv: 1.0,
      description: '대상의 체력(CON)을 증가시킵니다 (클레릭 특화 힘+체력 버프와 합산 중첩!).',
      growthSummary: '레벨당 체력 +1.0 증가 (60렙 시 +65) / 집중소모 +0.35',
      icon: '❤️',
    },
    8: {
      id: 8, name: 'Raise Dead', nameKr: '레이즈데드',
      spCost: 0, mpCost: 50, concCost: 0,
      isToggle: false, castTime: 3.0, cooldown: 180.0, cdPerLv: -2.5, range: 6.0,
      damageType: 'holy', basePower: 30,
      description: '쓰러진 아군을 부활시킵니다 (레벨 상승 시 재사용 대기시간 단축).',
      growthSummary: '레벨당 재사용 대기시간 -2.5초 감소 (60렙 시 32.5초)',
      icon: '✨',
    },
  },

  // ==========================================================================
  // g. 🐣 초보자 (Novice - 무직) - 슬롯 1 기본공격(평타) 전용
  // ==========================================================================
  novice: {
    1: {
      id: 1, name: 'Basic Attack', nameKr: '기본공격',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 1.2, range: 1.5,
      damageType: 'slash', basePower: 12, powerPerLv: 1.5,
      description: '초보자의 가장 기초적인 기본 일반 공격입니다. 무기나 주먹으로 대상을 타격합니다.',
      growthSummary: '레벨당 공격력 +1.5',
      icon: '⚔️',
    },
  },

  // ==========================================================================
  // h. 👑 관리자 (Admin - 최고 관리자) - 8종 전용 특수 스킬 (쿨타임 0초, 관리자 전용)
  // ==========================================================================
  admin: {
    1: {
      id: 1, name: 'Admin Attack', nameKr: '평타',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 0, range: 2.0,
      damageType: 'pure', basePower: 50, powerPerLv: 5,
      description: '관리자의 기본 평타 공격입니다. 쿨타임 없이 대상을 즉시 타격합니다.',
      growthSummary: '쿨타임 없음 / 관리자 전용',
      icon: '⚔️',
    },
    2: {
      id: 2, name: 'Admin Punch', nameKr: '주먹질',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 0, range: 1.8,
      damageType: 'crush', basePower: 1,
      description: '무기를 장착하고 있어도 데미지가 정확히 1만 들어가는 훈계용 관리자 주먹질입니다.',
      growthSummary: '무기 무관 고정 데미지 1 / 쿨타임 0초',
      icon: '👊',
    },
    3: {
      id: 3, name: 'Admin Full Heal', nameKr: '완치',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 0, range: 10.0,
      damageType: 'holy', basePower: 100,
      description: '타겟 또는 시전자 자신의 체력을 즉시 100% 완전 회복(완치)시킵니다.',
      growthSummary: '체력 100% 즉시 회복 / 쿨타임 0초',
      icon: '💖',
    },
    4: {
      id: 4, name: 'Admin Stun Toggle', nameKr: '스턴/해제',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 0, range: 12.0,
      damageType: 'pure', basePower: 0,
      description: '대상에게 기절(Stun)을 걸거나, 이미 기절 상태라면 즉시 해제하는 토글형 스킬입니다.',
      growthSummary: '기절 부여 및 즉시 해제 토글 / 쿨타임 0초',
      icon: '⚡',
    },
    5: {
      id: 5, name: 'Admin Mez Toggle', nameKr: '메즈/해제',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 0, range: 12.0,
      damageType: 'pure', basePower: 0,
      description: '대상에게 수면(Mez)을 걸거나, 이미 수면 상태라면 즉시 깨워 해제하는 토글형 스킬입니다.',
      growthSummary: '수면 부여 및 즉시 해제 토글 / 쿨타임 0초',
      icon: '💤',
    },
    6: {
      id: 6, name: 'Admin Slow Toggle', nameKr: '슬로우',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 0, range: 12.0,
      damageType: 'pure', basePower: 0,
      description: '대상의 이동속도를 75% 대폭 감속(Slow)시키거나, 이미 슬로우 상태라면 즉시 해제합니다.',
      growthSummary: '이동속도 대폭 감속 / 즉시 해제 토글 / 쿨타임 0초',
      icon: '🐌',
    },
    7: {
      id: 7, name: 'Admin Jail', nameKr: '감옥',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 0, range: 12.0,
      damageType: 'pure', basePower: 0,
      description: '확인창(Confirm)을 거쳐 대상을 즉시 솔라리스 지하 감옥으로 강제 수감 격리하거나 영구 구속합니다.',
      growthSummary: '수감 확인창 후 지하 감옥 수감 격리 / 쿨타임 0초',
      icon: '⛓️',
    },
    8: {
      id: 8, name: 'Admin Instant Kill', nameKr: '즉사',
      spCost: 0, mpCost: 0, concCost: 0,
      isToggle: false, castTime: 0, cooldown: 0, range: 12.0,
      damageType: 'pure', basePower: 999999,
      description: '타겟 대상의 체력을 0으로 만들어 단 한 방에 즉사 처치합니다.',
      growthSummary: '타겟 1방 즉사 처형 / 쿨타임 0초',
      icon: '☠️',
    },
  },
};

export const SKILL_DEFINITIONS: Record<number, SkillDefinition> = CLASS_SKILL_DEFINITIONS.tank;
